/**
 * Date utility functions for safe date handling throughout the application
 */

import { format, type Locale } from 'date-fns';
import { enUS, ja } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import { translateWithFallback } from './translation';

const DATE_FNS_LOCALES = { ja, en: enUS } as const;

/**
 * date-fns localized tokens. A numeric pattern like 'yyyy/MM/dd' renders the
 * same bytes in every locale, so threading the locale would be a no-op — these
 * are what make the locale actually reach the output. Japanese is unchanged
 * (2026/08/06 14:30); English becomes 08/06/2026, 2:30 PM.
 */
export const DATE_FORMAT = 'P';
export const DATE_TIME_FORMAT = 'Pp';

/**
 * Interface-language inputs. Callers thread `t` and `language` from
 * `useTranslation()`; omitting them keeps the historical Japanese output.
 */
export interface DateLocalization {
  t?: TFunction;
  language?: string;
}

const resolveDateLocale = (language?: string): Locale => (
  DATE_FNS_LOCALES[language?.slice(0, 2) as keyof typeof DATE_FNS_LOCALES] ?? ja
);

const localize = (
  t: TFunction | undefined,
  key: string,
  fallback: string,
): string => (t ? translateWithFallback(t, key, fallback) : fallback);

/**
 * Safely format a date string to display format
 * @param dateString - Date string to format
 * @param formatString - Format string (default: 'yyyy/MM/dd')
 * @param i18n - Optional interface language / translator
 * @returns Formatted date string or fallback message
 */
export const formatDateSafe = (
  dateString: string | null | undefined,
  formatString: string = DATE_FORMAT,
  i18n: DateLocalization = {}
): string => {
  const unknownDate = () => localize(i18n.t, 'common.dateUnknown', '日付不明');
  if (!dateString) return unknownDate();

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return unknownDate();
    }
    return format(date, formatString, { locale: resolveDateLocale(i18n.language) });
  } catch (error) {
    console.warn('Invalid date format:', dateString, error);
    return unknownDate();
  }
};

/**
 * Get the best available date from a document object
 * @param doc - Document object with various date fields
 * @returns Best available date string or null
 */
export const getBestDate = (doc: any): string | null => {
  return doc.uploadedAt ||
         doc.createdAt ||
         doc.updatedAt ||
         doc.lastModified ||
         null;
};

/**
 * Create a Date object from various date string formats safely
 * @param dateString - Date string to parse
 * @returns Valid Date object or null if invalid
 */
export const createDateSafe = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.warn('Invalid date format:', dateString, error);
    return null;
  }
};

/**
 * Format file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number | undefined | null): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
